import {useAuth0} from '@auth0/auth0-react';
import {
  Avatar,
  Popover,
  PopoverButton,
  PopoverContent,
  PopoverTrigger,
  Typography
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {AccountNav} from './AccountNav';
import {DarkModeToggle} from './DarkModeToggle';
import {SidebarSectionLabel} from './SidebarSectionLabel';
import {ThemeSwitcher} from '../../theme/components/ThemeSwitcher';
import {getInitials} from '../../utils/get-initials.utils';
import {useUserDetails} from '../hooks';
import {clearRememberedOrganization} from '../utils/remembered-organization.utils';

import type {AccountNavFirm} from './AccountNav';
import type {FC} from 'react';

export interface AccountMenuProps {
  claimsNamespace: string;
  logoutReturnTo?: string;
  role?: string;
  /** Shows a skeleton in place of `role` while it's still resolving. */
  isRoleLoading?: boolean;
  ns?: string;
  /**
   * Renders an "Account settings" item in the accounts card (above the display section) when
   * provided. Left unset, the menu is unchanged — e.g. ops-web mounts this same component with
   * no settings entry.
   */
  onOpenAccountSettings?: () => void;
  /**
   * Renders a "Firm settings" item alongside account settings. Unlike `onOpenAccountSettings`,
   * every firm member can view firm settings (editing is gated separately inside the modal), so
   * this is expected to always be provided by `web` — left optional only so `ops-web`, which has
   * no firm-settings modal, is unaffected.
   */
  onOpenFirmSettings?: () => void;
  /** Shows an "Admin" badge next to the firm settings item — the viewer can edit, not just view. */
  canEditFirmSettings?: boolean;
  /** Overrides the Auth0-claim name/picture. Once provided, treated as complete — an empty `picture` renders initials, never falls back to the claim. */
  profile?: {name?: string; picture?: string};
  /** Leads the sidebar card with the firm's logo and name (user avatar overlaid). Unset → user-only card. */
  firm?: Omit<AccountNavFirm, 'initials'>;
}

export const AccountMenu: FC<AccountMenuProps> = ({
  claimsNamespace,
  logoutReturnTo = window.location.origin,
  role,
  isRoleLoading,
  ns = 'common',
  onOpenAccountSettings,
  onOpenFirmSettings,
  canEditFirmSettings,
  profile,
  firm
}) => {
  const {logout} = useAuth0();
  const {user, name, email, picture} = useUserDetails(claimsNamespace);
  const {t} = useTranslation(ns);

  const displayName = (profile ? profile.name : name) ?? email ?? '';
  const displayPicture = profile ? profile.picture : picture;
  const initials = getInitials(displayName);

  const handleLogout = () => {
    clearRememberedOrganization();
    void logout({logoutParams: {returnTo: logoutReturnTo}});
  };

  return (
    <Popover>
      <div className="hidden group-data-[collapsible=icon]:flex items-center justify-center">
        <ThemeSwitcher />
      </div>
      <PopoverTrigger
        render={
          <AccountNav
            name={displayName}
            role={role}
            isRoleLoading={isRoleLoading}
            initials={initials}
            src={displayPicture}
            seed={user?.sub}
            firm={firm && {...firm, initials: getInitials(firm.name)}}
          />
        }
      />
      <PopoverContent side="top" align="start" className="w-(--anchor-width) min-w-60 p-0">
        <div data-slot="account-menu" className="overflow-hidden rounded-xl bg-background">
          <div
            data-slot="account-menu-card"
            className="rounded-t-xl rounded-b-2xl bg-background-surface border-b border-border"
          >
            <SidebarSectionLabel>{t('accountMenu.accounts')}</SidebarSectionLabel>
            <div className="flex items-center gap-3 px-3 py-3">
              <Avatar
                size="lg"
                initials={initials}
                alt={displayName}
                src={displayPicture}
                seed={user?.sub}
              />
              <div data-slot="account-menu-text" className="flex min-w-0 flex-1 flex-col">
                <Typography size="sm" weight="semibold" className="truncate">
                  {displayName}
                </Typography>
                <Typography size="sm" color="tertiary" className="truncate">
                  {email}
                </Typography>
              </div>
            </div>
            {onOpenAccountSettings && (
              <div className="px-1.5 pb-1.5">
                <PopoverButton
                  data-slot="account-menu-settings"
                  icon="settings"
                  onClick={onOpenAccountSettings}
                >
                  {t('accountMenu.settings')}
                </PopoverButton>
              </div>
            )}
            {onOpenFirmSettings && (
              <div className="px-1.5 pb-1.5">
                <PopoverButton
                  data-slot="account-menu-firm-settings"
                  icon="building-05"
                  badge={canEditFirmSettings ? t('accountMenu.adminBadge') : undefined}
                  onClick={onOpenFirmSettings}
                >
                  {t('accountMenu.firmSettings')}
                </PopoverButton>
              </div>
            )}
            <SidebarSectionLabel>{t('accountMenu.display')}</SidebarSectionLabel>
            <div className="px-1.5 pb-1.5">
              <DarkModeToggle />
            </div>
          </div>

          <div data-slot="account-menu-footer" className="px-1.5 pb-1.5 pt-1">
            <PopoverButton data-slot="account-menu-logout" icon="logout" onClick={handleLogout}>
              {t('logout')}
            </PopoverButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
